import _, {concat, findLast, first, last, partition} from 'lodash';
import React, {Component, FC, Fragment, Key, RefCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {useDebounce} from '../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {onlyItem} from '../../../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {Loader, LoaderVariantsEnum} from '../../../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../srcCommon/browser/openInBrowser';
import {computeIsDraftInReadMode} from '../../../../../../srcCommon/helpers/messageDraftHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {filterStaticViewerRelatedActivities} from '../../../../../../srcCommon/inboxes/viewer/activity/static/staticViewerHelpers';
import {
  findFocusKey,
  findLastTimelineMessage,
  LastTimelineMessage,
  maybeUpdateFirstVisibleActivityDate,
  maybeUpdateLastVisibleActivityDate,
  MILESTONE_ACTIVITY,
} from '../../../../../../srcCommon/inboxes/viewer/conversation/timeline/viewerTimelineHelpers';
import {usePush} from '../../../../../../srcCommon/push/pushContext';
import {
  FocusModesEnum,
  LoadMoreHandler,
  ScrollDirectionsEnum,
} from '../../../../../../srcCommon/scroll/scrollerTypes';
import {startSimplePerformanceInteraction} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformance';
import {PerformanceInteractionTypesEnum} from '../../../../../../srcCommon/services/performanceMetrics/interactionPerformanceTypes';
import {useMeasureElement} from '../../../../../helpers/hooks/domHookHelpers';
import {isHighlightComment} from '../../../../../helpers/models/commentHelpers';
import {
  isActivityInConversationRelevant,
  isConversationInTeamContext,
  makeConversationActionContext,
} from '../../../../../helpers/models/conversationHelpers';
import {isMessageVisuallyShared} from '../../../../../helpers/models/messageHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  BasicActivitySplitTargetModel,
  BasicActivityTypesEnum,
  BasicVisualTypesEnum,
  MessageActivityTypesEnum,
} from '../../../../../models/api/activityModel';
import {
  ActivitySourceTypesEnum,
  ActivitySourceVisualTypesEnum,
} from '../../../../../models/api/activitySourceModel';
import {ChannelModel} from '../../../../../models/api/channelModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {InboxModel} from '../../../../../models/api/inboxModel';
import {DraftMessageModel, TimelineMessageModel} from '../../../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../models/api/recipientModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {AsyncMoreStatusesEnum, ResourceMap} from '../../../../../store/appTypes';
import {isBasicActivityCollapsible} from '../../../../../store/data/activities/activitiesCollapseSelectors';
import {FindTeammate} from '../../../../../store/data/teammates/lazyTeammatesSelectors';
import {CellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {findParametersActivityId} from '../../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {ViewerPanelId} from '../../../../../store/panels/panelStateBase';
import {SendingActivity} from '../../../../../store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelConversationPageState} from '../../../../../store/panels/viewer/viewerPanelState';
import {helpCenterUrl} from '../../../../../store/stateless/navigationActions';
import {TimelineVariationsEnum} from '../../../../../store/timelineVariations/timelineVariationSelectors';
import {Highlighter} from '../../../../common/highlighter';
import {DynamicScrollBoundaries} from '../../../../common/scroll/dynamicScrollBoundaries';
import {VariableHeightScroller} from '../../../../common/scroll/variableHeightScroller';
import {InfiniteScrollerProps} from '../../../../common/scroll/variableHeightScrollerTypes';
import {ActivityViewer} from '../../activity/activityViewer';
import {BasicViewerGroup} from '../../activity/basic/basicViewerGroup';
import {ChatPrecogContainer} from '../../activity/chat/chatPrecogContainer';
import {CommentPrecog} from '../../activity/comment/commentPrecog';
import {DuplicateConversationBannerContainer} from '../../activity/duplicateConversationBanner/duplicateConversationBannerContainer';
import {BottomMessageDrafts} from '../../activity/message/bottomMessageDrafts';
import {MessageDrafts} from '../../activity/message/messageDrafts';
import {MessagePrecog} from '../../activity/message/messagePrecog';
import {MessageViewerContainer} from '../../activity/message/messageViewerContainer';
import {GoOnlineBanner, GoOnlineBannerRenderLocationsEnum} from '../../activity/offline/goOnlineBanner';
import {LastSyncedActivity} from '../../activity/offline/lastSyncedActivity';
import {SendingActivityViewer} from '../../activity/sendingActivityViewer';
import {SplitConversationBannerContainer} from '../../activity/splitConversationBanner/splitConversationBannerContainer';
import {StaticViewerContainer} from '../../activity/static/staticViewerContainer';
import {MessageComposerContainer} from '../../messageComposer/messageComposerContainer';
import {UnreadBannerAboveContainer} from '../../unreadBanners/unreadBannerAboveContainer';
import {UnreadBannerBelowContainer} from '../../unreadBanners/unreadBannerBelowContainer';
import {ConversationTimelineMarkAsReadOnLoad} from './conversationTimelineMarkAsReadOnLoad';
import {ConversationTimelineMilestone} from './conversationTimelineMilestone';
import {ConversationTimelineSeenMarkerContainer} from './conversationTimelineSeenMarkerContainer';
import {DemoConversationBanner} from './demoConversationBanner/demoConversationBanner';
import {useOpenComposerByDefaultEffect} from './hooks/synchronousChannels';
import {RecentCommunicationsBannerContainer} from './recentCommunicationsBanner/recentCommunicationsBannerContainer';

/*
 * Constants.
 */

const helpCenterDataLeakUrl = `${helpCenterUrl}/t/m2bx52/how-smart-merge-works#why-did-my-smart-merged-conversation-split-into-a-new-conversation`;

/*
 * Props.
 */

export interface ConversationTimelineProps {
  panelId: ViewerPanelId;
  userId: number;
  userAlias: string;
  page: ViewerPanelConversationPageState;
  conversation: ConversationModel;
  inboxes: ResourceMap<InboxModel>;
  findTeammate: FindTeammate;

  activities: ReadonlyArray<ActivityModel>;
  orphanSentMessages: ReadonlyArray<DraftMessageModel | TimelineMessageModel>;
  sendingActivities: ReadonlyArray<SendingActivity>;
  commentingTeammates: ReadonlyArray<TeammateModel>;
  hasChatPrecog: boolean;
  replyingTeammates: ReadonlyArray<TeammateModel>;
  draftSeedAuthors: ReadonlyArray<TeammateModel>;
  composeDraftMessages: ReadonlyArray<DraftMessageModel>;
  userComposeDraftMessages: ReadonlyArray<DraftMessageModel>;
  readonlyDraftMessageUids: ReadonlySet<string>;
  lastActivityCommentAuthorId?: number;
  canceledReminder?: ReminderModel;
  messageUidsWithNewCalendarEvents: ReadonlySet<string>;

  replyChannel?: ChannelModel;
  dontFocusComposer?: boolean;
  fetchMore: LoadMoreHandler;
  deleteEmptyDrafts: Handler;

  isSpam: boolean;
  fetchedAt: number | undefined;

  hasRecentCommunicationsBanner: boolean;
  hasRecentCommunicationsComposerBanner: boolean;

  isPersonalizedDemoInboxActive: boolean;

  hasAutoShareDrafts: boolean;
  timelineVariation: TimelineVariationsEnum;
}

/*
 * Style.
 */

const StyledTimelineScrollBoundaries = styled(DynamicScrollBoundaries)`
  position: relative;
  grid-area: timeline;
`;

const StyledTimelineScroller = styled(VariableHeightScroller)<{
  $showDemoConversationBanner: boolean;
}>`
  & > div {
    padding: ${(p) => (p.$showDemoConversationBanner ? '0 16px 66px 16px' : '0 16px 16px 16px')};
    padding-left: 6px;
    padding-right: 14px;

    display: flex;
    flex-direction: column;
  }
`;

const StyledLoaderDiv = styled.div`
  height: 40px;
  ${center()};
  margin: 8px 0 -8px 0;
`;

const StyledMainHighlighter = styled(Highlighter)`
  background-color: ${(p) => p.theme.palette.yellow.highlight};
  color: ${(p) => p.theme.highlightText};
`;

/*
 * Component.
 */

interface ConversationTimelineState {
  focusKey?: Key;
  jumpUid: string | undefined;
  lastDraftComposeUid?: string;
  lastSendingCommentActivityUid?: string;
  isLoadingBottom: boolean;
}

const acceptedModifiersForHighlight = [
  ModifiersEnum.RECIPIENT,
  ModifiersEnum.FROM,
  ModifiersEnum.TO,
  ModifiersEnum.CC,
  ModifiersEnum.BCC,
  ModifiersEnum.SUBJECT,
  ModifiersEnum.BODY,
];

export class ConversationTimeline extends Component<ConversationTimelineProps, ConversationTimelineState> {
  constructor(props: ConversationTimelineProps) {
    super(props);

    // Set existing last draft uid on load to prevent scrolling to an already open draft if unread messages
    const lastDraftCompose = last(props.composeDraftMessages);
    const lastDraftComposeUid = lastDraftCompose && lastDraftCompose.uid;

    this.state = {
      focusKey: findFocusKey(props),
      jumpUid: props.page.jumpUid,
      lastDraftComposeUid,
      isLoadingBottom: false,
    };
  }

  /*
   * Lifecycle.
   */

  componentWillUnmount() {
    this.maybeDeleteEmptyDrafts();
  }

  /*
   * Delete unsent draft logic.
   */

  private maybeDeleteEmptyDrafts() {
    if (this.props.replyChannel && this.props.replyChannel.behaviors.alwaysShowReplyComposer) {
      this.props.deleteEmptyDrafts();
    }
  }

  /*
   * Focus scroll logic.
   */

  static getDerivedStateFromProps(
    props: Readonly<ConversationTimelineProps>,
    state: Readonly<ConversationTimelineState>,
  ): Partial<ConversationTimelineState> | null {
    const isLoadingBottom = props.page.moreStatusBottom === AsyncMoreStatusesEnum.LOADING;
    const isAllLoadedBottom = props.page.moreStatusBottom === AsyncMoreStatusesEnum.ALL_LOADED;
    const {jumpUid} = props.page;
    const derivedState: Partial<ConversationTimelineState> = {
      jumpUid,
      isLoadingBottom,
    };

    // If there's a new draft, focus it.
    const lastDraftCompose = last(props.composeDraftMessages);
    const lastDraftComposeUid = lastDraftCompose && lastDraftCompose.uid;
    if (
      isAllLoadedBottom &&
      !state.isLoadingBottom &&
      lastDraftComposeUid &&
      lastDraftComposeUid !== state.lastDraftComposeUid
    ) {
      return {
        ...derivedState,
        // Don't focus on the draft if we're viewing the demo conversation.
        focusKey: props.conversation.isGuidedDemo ? state.focusKey : lastDraftComposeUid,
        lastDraftComposeUid,
      };
    }

    // If there's a new comment from our user, focus it (unless it's a highlight).
    const lastSendingCommentActivity = findLast(props.sendingActivities, (a) =>
      Boolean(a.comment && a.comment.authorId === props.userId && !isHighlightComment(a.comment)),
    );
    if (
      lastSendingCommentActivity &&
      lastSendingCommentActivity.uid !== state.lastSendingCommentActivityUid
    ) {
      return {
        ...derivedState,
        focusKey: lastSendingCommentActivity.uid,
        lastSendingCommentActivityUid: lastSendingCommentActivity.uid,
      };
    }

    // If the content page indicates a jump, focus on that activity.
    if (jumpUid !== state.jumpUid) {
      return {
        ...derivedState,
        focusKey: findFocusKey(props),
        lastDraftComposeUid,
      };
    }

    return {
      ...derivedState,
      lastDraftComposeUid,
    };
  }

  /*
   * Render.
   */

  render() {
    const {page, fetchMore, activities, orphanSentMessages, sendingActivities, composeDraftMessages} =
      this.props;
    const conversationId = this.props.conversation.id;

    const {moreStatusTop, moreStatusBottom, cellsContext} = page;
    const isAllLoadedBottom = moreStatusBottom === AsyncMoreStatusesEnum.ALL_LOADED;
    const firstActivity = first(activities);
    const firstActivityId = firstActivity?.id || undefined;

    // Find the first message activity.
    const firstMessageActivity =
      page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED &&
      activities.find((a) => a.visualType === ActivityVisualTypesEnum.MESSAGE);
    const firstMessageActivityId = (firstMessageActivity && firstMessageActivity.id) || undefined;

    // Find the first comment activity.
    const firstCommentActivity =
      page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED &&
      activities.find((a) => a.visualType === ActivityVisualTypesEnum.COMMENT);
    const firstCommentActivityId = (firstCommentActivity && firstCommentActivity.id) || undefined;

    // Find the last message activity.
    const hasSendingMessageActivity = sendingActivities.some((c) => Boolean(c.message));
    const hasDraftMessage = composeDraftMessages.length > 0;
    const lastTimelineMessage = findLastTimelineMessage(
      activities,
      orphanSentMessages,
      sendingActivities,
      hasDraftMessage,
    );

    // find the last inbound message activity
    const lastIncomeMessageActivity =
      page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED &&
      findLast(
        activities,
        (a) =>
          a.visualType === ActivityVisualTypesEnum.MESSAGE && a.type === MessageActivityTypesEnum.INBOUND,
      );
    const lastIncomeMessageActivityId =
      (lastIncomeMessageActivity && lastIncomeMessageActivity.id) || undefined;

    // Infinite scroll logic.
    const infinite: InfiniteScrollerProps = {
      isLoadingMoreTop: moreStatusTop === AsyncMoreStatusesEnum.LOADING,
      hasMoreTop: moreStatusTop === AsyncMoreStatusesEnum.HAS_MORE,
      hasMoreBottom: moreStatusBottom === AsyncMoreStatusesEnum.HAS_MORE,
      loadMoreThreshold: 100,
      loadMore: (direction) => fetchMore(direction),
      loaderRenderer: (direction) => renderLoader(page, direction),
    };

    const {focusKey} = this.state;
    const focusMode =
      focusKey === findParametersActivityId(page.parameters) ? FocusModesEnum.MIDDLE : FocusModesEnum.TOP;
    return (
      <>
        <ConversationTimelineMarkAsReadOnLoad cellsContext={cellsContext} conversationId={conversationId} />
        <ConversationTimelineSeenMarkerContainer
          cellsContext={cellsContext}
          conversationId={conversationId}
        />
        <ConversationTimelineContent
          {...this.props}
          {...this.state}
          hasSendingMessageActivity={hasSendingMessageActivity}
          focusMode={focusMode}
          infinite={infinite}
          firstActivityId={firstActivityId}
          firstMessageActivityId={firstMessageActivityId}
          lastIncomeMessageActivityId={lastIncomeMessageActivityId}
          lastTimelineMessage={lastTimelineMessage}
          firstCommentActivityId={firstCommentActivityId}
          query={cellsContext.searchQuery}
          isAllLoadedBottom={isAllLoadedBottom}
        />
      </>
    );
  }
}

/*
 * Sub-Component.
 */

interface ConversationTimelineContentProps extends ConversationTimelineProps, ConversationTimelineState {
  hasSendingMessageActivity: boolean;
  focusMode: FocusModesEnum;
  infinite: InfiniteScrollerProps;
  firstActivityId?: number;
  firstMessageActivityId?: number;
  lastIncomeMessageActivityId?: number;
  lastTimelineMessage: LastTimelineMessage;
  firstCommentActivityId?: number;
  query?: string;
  isAllLoadedBottom: boolean;
  replyChannel?: ChannelModel;
  deleteEmptyDrafts: Handler;
  timelineVariation: TimelineVariationsEnum;
}

const ConversationTimelineContent: FC<ConversationTimelineContentProps> = (props) => {
  const theme = useUserTheme();
  const originalLastActivityDate = props.page.originalConversationState?.lastActivityDate ?? Infinity;

  const [lastVisibleActivityDate, setLastVisibleActivityDate] = useState(0);
  const [firstVisibleActivityDate, setFirstVisibleActivityDate] = useState(originalLastActivityDate);
  const [isAtBottomOfTimeline, setIsAtBottomOfTimeline] = useState(true);
  const [isAtTopOfTimeline, setIsAtTopOfTimeline] = useState(true);
  const [hasMultipleMessagesVisible, setHasMultipleMessagesVisible] = useState(true);
  const {isConnected} = usePush();

  const {
    isSpam,
    activities,
    page,
    focusMode,
    infinite,
    firstMessageActivityId,
    lastIncomeMessageActivityId,
    lastTimelineMessage,
    isAllLoadedBottom,
    firstCommentActivityId,
    firstActivityId,
    query = '',
    composeDraftMessages,
    panelId,
    conversation,
    replyChannel,
    timelineVariation,
    userId,
  } = props;

  const {originalConversationState} = page;
  const hasOriginalUnreadActivities = useMemo(() => {
    if (!originalConversationState) {
      return false;
    }

    const {seenAt, lastActivityDate} = originalConversationState;
    return activities.some(
      (a) => !seenAt || !lastActivityDate || (seenAt < a.date && lastActivityDate >= a.date),
    );
  }, [activities, originalConversationState]);

  // Separate read activities from unread ones.
  const [unreadActivities, readActivities] = useMemo(() => {
    if (!originalConversationState) {
      return [[], activities] as const;
    }

    const {seenAt} = originalConversationState;
    return partition(activities, (a) => !seenAt || seenAt < a.date);
  }, [activities, originalConversationState]);

  const shouldOpenComposerByDefault = timelineVariation === TimelineVariationsEnum.SYNCHRONOUS;

  // This hook creates an empty draft by default for a conversation, which opens the composer
  // It is only enabled for synchronous channels that have their specific channel FF enabled as well.
  useOpenComposerByDefaultEffect(
    panelId,
    composeDraftMessages,
    conversation,
    userId,
    isAllLoadedBottom,
    shouldOpenComposerByDefault,
    replyChannel,
  );

  const updateLastVisibleActivityDebounced = useDebounce((computeVisibleItems: () => ReadonlyArray<Key>) => {
    if (!activities.length) {
      return;
    }

    const visibleActivityIds = computeVisibleItems();
    const firstVisibleActivityId = first(visibleActivityIds);
    const lastVisibleActivityId = last(visibleActivityIds);
    if (!lastVisibleActivityId) {
      return;
    }

    if (visibleActivityIds.length <= 1) {
      setHasMultipleMessagesVisible(false);
    } else {
      setHasMultipleMessagesVisible(true);
    }

    const lastVisibleActivity = activities.find((a) => a.id.toString() === lastVisibleActivityId);
    const firstVisibleActivity = activities.find((a) => a.id.toString() === firstVisibleActivityId);
    const firstActivity = first(activities);

    maybeUpdateFirstVisibleActivityDate(
      page,
      firstVisibleActivityDate,
      setFirstVisibleActivityDate,
      setIsAtTopOfTimeline,
      firstActivity,
      firstVisibleActivity,
    );

    maybeUpdateLastVisibleActivityDate(
      lastVisibleActivityDate,
      lastVisibleActivityId,
      isAtBottomOfTimeline,
      setIsAtBottomOfTimeline,
      setLastVisibleActivityDate,
      firstVisibleActivity,
      lastVisibleActivity,
    );
  }, 200);

  // Allow us to keep the size of the draft placeholder consistent with the sticky composer.
  const [bottomComposerRef, {height: measuredBottomComposerHeight}] = useMeasureElement();
  // Although the measured height should be 0 in the ASYNCHRONOUS case, we still default it to 0.
  const bottomComposerHeight =
    timelineVariation === TimelineVariationsEnum.SYNCHRONOUS ? measuredBottomComposerHeight : 0;

  return (
    <StyledTimelineScrollBoundaries hasBottomBoundary={false} color={theme.greys.shade30}>
      <StyledTimelineScroller
        $showDemoConversationBanner={props.isPersonalizedDemoInboxActive}
        keepScrollDirection={ScrollDirectionsEnum.BOTTOM}
        jumpKey={page.jumpUid}
        focusKey={props.focusKey}
        focusMode={focusMode}
        focusMargin={findFocusMargin(props, props.focusKey)}
        infinite={infinite}
        onVisibleItemsChange={updateLastVisibleActivityDebounced}
        interactionId="conversation-timeline"
        onScroll={(event) =>
          startSimplePerformanceInteraction(event, PerformanceInteractionTypesEnum.VIEWER_SCROLL)
        }
      >
        {maybeRenderGoOnlineBanner(props, isConnected) || []}
        {maybeRenderSplitConversationBanner(props) || []}
        {/* Note that StyledTimelineScroller expects all these scrollable children to have matching keys and be rendered at the same, root level */}
        {renderActivities({
          props,
          isSpam,
          lastVisibleActivityDate,
          originalLastActivityDate,
          isAllVisible: isAtBottomOfTimeline && isAtTopOfTimeline,
          hasOriginalUnreadActivities,
          unreadActivities,
          readActivities,
          firstMessageActivityId,
          lastIncomeMessageActivityId,
          lastTimelineMessage,
          firstCommentActivityId,
          firstActivityId,
          focusKey: props.focusKey,
        })}
        {(isAllLoadedBottom && renderAllLoadedBottom(props, isSpam, isConnected, lastTimelineMessage)) || []}
      </StyledTimelineScroller>
      {renderBottomDrafts(props, bottomComposerRef, bottomComposerHeight)}
      <StyledMainHighlighter query={query} acceptedModifiers={acceptedModifiersForHighlight} />
      {!isAtTopOfTimeline && (
        <UnreadBannerAboveContainer
          panelId={props.panelId}
          activities={activities}
          firstVisibleActivityDate={firstVisibleActivityDate}
          lastVisibleActivityDate={lastVisibleActivityDate}
          hasOnlyLastMessageInView={isAtBottomOfTimeline && !hasMultipleMessagesVisible}
          page={page}
        />
      )}
      {!isAtBottomOfTimeline && (
        <UnreadBannerBelowContainer
          panelId={props.panelId}
          activities={activities}
          lastVisibleActivityDate={lastVisibleActivityDate}
          page={page}
          originalLastActivityDate={originalLastActivityDate}
          bottomComposerHeight={bottomComposerHeight}
        />
      )}
      {maybeRenderDemoConversationBanner(props)}
    </StyledTimelineScrollBoundaries>
  );
};

/*
 * Section renderers.
 */

interface RenderActivitiesOptions {
  props: ConversationTimelineContentProps;
  isSpam: boolean;
  lastVisibleActivityDate: number;
  originalLastActivityDate: number;
  isAllVisible: boolean;
  hasOriginalUnreadActivities: boolean;
  unreadActivities: ReadonlyArray<ActivityModel>;
  readActivities: ReadonlyArray<ActivityModel>;
  firstActivityId?: number;
  firstMessageActivityId?: number;
  lastIncomeMessageActivityId?: number;
  lastTimelineMessage: LastTimelineMessage;
  firstCommentActivityId?: number;
  focusKey?: Key;
}

function renderActivities(options: RenderActivitiesOptions) {
  const {
    props,
    isSpam,
    lastVisibleActivityDate,
    originalLastActivityDate,
    isAllVisible,
    hasOriginalUnreadActivities,
    unreadActivities,
    readActivities,
    firstMessageActivityId,
    lastIncomeMessageActivityId,
    firstCommentActivityId,
    lastTimelineMessage,
    firstActivityId,
    focusKey,
  } = options;
  const {page, conversation, activities} = props;
  const isOnlyMessage = conversation.numMessages === 1;
  const isOnlyActivity = isOnlyMessage && !conversation.lastComment;
  const lastActivity = findLast(activities, isActivityInConversationRelevant);
  const hasActivitiesBelow =
    props.orphanSentMessages.length ||
    props.sendingActivities.length ||
    props.commentingTeammates.length ||
    props.replyingTeammates.length ||
    props.draftSeedAuthors.length;

  const canceledReminderActivityId = findCanceledReminderActivity(
    activities,
    props.canceledReminder,
    lastTimelineMessage.activityId,
  );

  const originalSeenAt = page.originalConversationState ? page.originalConversationState.seenAt : Infinity;

  // Build a method to render an array of activities.
  const renderActivityArray = (array: ReadonlyArray<ActivityModel>) => {
    const activityGroups = array
      .filter((...args) => filterStaticViewerRelatedActivities(conversation, page, ...args))
      .reduce(
        (currentActivityGroups, activity) => {
          if (
            activity.visualType === ActivityVisualTypesEnum.BASIC &&
            isBasicActivityCollapsible(props.userId, props.findTeammate, props.inboxes, activity)
          ) {
            // Sequential low-priority activities are grouped together.
            currentActivityGroups[currentActivityGroups.length - 1].push(activity);
          }
          // Non-basic activities always belong to their own group.
          else {
            currentActivityGroups.push([activity], []);
          }

          return currentActivityGroups;
        },
        [[]] as Array<Array<ActivityModel>>,
      )
      // There may be empty arrays from sequential non-basic activities, which we filter out here.
      .filter((activityGroup) => activityGroup.length);

    // <ActivityViewer /> requires the overall index for each activity, even when separated into groups.
    return activityGroups.map((activityGroup, groupIndex) => {
      // True start index of this activity group can be computed from size of all previous groups.
      const activityIndexStart = activityGroups.slice(0, groupIndex).reduce((a, b) => a + b.length, 0);

      const activityViewers = activityGroup.map((activity, activityIndex) => {
        const index = activityIndexStart + activityIndex;

        return (
          <ActivityViewer
            key={activity.id}
            focusKey={focusKey}
            panelId={props.panelId}
            cellsContext={page.cellsContext}
            conversationId={conversation.id}
            conversationActionContext={makeConversationActionContext(page.cellsContext, conversation)}
            isSpam={isSpam}
            namespace={conversation.namespace}
            replyChannel={lastTimelineMessage.activityId === activity.id ? props.replyChannel : undefined}
            activities={array}
            activityStates={page.activities}
            activityIndex={index}
            isUnread={!originalSeenAt || originalSeenAt < activity.date}
            isNewUnread={originalLastActivityDate < activity.date}
            isVisible={isAllVisible || lastVisibleActivityDate >= activity.date}
            isFirstMessage={activity.id === firstMessageActivityId && activity.id === firstActivityId}
            isFirstComment={activity.id === firstCommentActivityId && activity.id === firstActivityId}
            isLastActivity={lastActivity && activity.id === lastActivity.id && !hasActivitiesBelow}
            isFirstActivity={activity.id === firstActivityId}
            isOnlyMessage={isOnlyMessage}
            lastIncomeMessageActivityId={lastIncomeMessageActivityId}
            canceledReminder={
              (activity.id === canceledReminderActivityId && props.canceledReminder) || undefined
            }
            isOnlyActivity={isOnlyActivity}
            userId={props.userId}
            isPersonalizedDemoInboxActive={props.isPersonalizedDemoInboxActive}
            jumpKey={page.jumpUid}
            jumpSourceActivityId={page.jumpSourceActivityId}
            isHighlighted={activity.id === findParametersActivityId(page.parameters)}
            containsNewCalendarEvent={containsNewCalendarEvent(props, activity)}
            timelineVariation={props.timelineVariation}
          />
        );
      });

      // If this is not a basic activity group, do not wrap in a group div.
      if (activityGroup.length === 1 && activityGroup[0].visualType !== ActivityVisualTypesEnum.BASIC) {
        return activityViewers;
      }

      // For a group of basic activities, wrap in a div to align by widest content and left-align.
      // The grid attributes for a basic activity row are set in <BasicViewerBase />
      const key = `activity_group_${activityGroup[activityGroup.length - 1].id}`;
      return <BasicViewerGroup key={key}>{activityViewers}</BasicViewerGroup>;
    });
  };
  // Check if we have any activities that were unread before opening the conversation.
  if (!hasOriginalUnreadActivities) {
    return renderActivityArray(activities);
  }

  // If there are no unread activities, just render the read ones.
  if (unreadActivities.length === 0) {
    return renderActivityArray(readActivities);
  }

  // If there are no read activities, just render the unread ones.
  if (readActivities.length === 0) {
    return renderActivityArray(unreadActivities);
  }

  // Otherwise, render both with a milestone in the middle.
  return [
    ...renderActivityArray(readActivities),
    <ConversationTimelineMilestone key={MILESTONE_ACTIVITY}>
      <Trans id="timeline-newactivity">New activity</Trans>
    </ConversationTimelineMilestone>,
    ...renderActivityArray(unreadActivities),
  ];
}

function renderAllLoadedBottom(
  props: ConversationTimelineContentProps,
  isSpam: boolean,
  isConnected: boolean,
  lastTimelineMessage: LastTimelineMessage,
) {
  const {conversation, orphanSentMessages, composeDraftMessages, timelineVariation} = props;

  const {cellsContext} = props.page;
  const isInTeamContext = isConversationInTeamContext(cellsContext, conversation);

  // Find the last activity.
  const lastActivity = last(props.activities);
  const lastActivityMessageUid =
    lastActivity?.visualType === ActivityVisualTypesEnum.MESSAGE ? lastActivity.messageUid : undefined;

  return concat(
    // Messages that have been sent, but don't have an activity or loaded/timeline message yet.
    orphanSentMessages.map((message, index) => (
      <MessageViewerContainer
        key={message.uid}
        panelId={props.panelId}
        cellsContext={cellsContext}
        namespace={conversation.namespace}
        conversationId={conversation.id}
        conversationActionContext={makeConversationActionContext(cellsContext, conversation)}
        isSpam={isSpam}
        replyChannel={
          lastTimelineMessage.orphanSentMessageUid === message.uid ? props.replyChannel : undefined
        }
        timelineVariation={timelineVariation}
        messageUid={message.uid}
        previousMessageUid={index === 0 ? lastActivityMessageUid : orphanSentMessages[index - 1].uid}
      />
    )),
    // Messages and comments that are sending.
    renderSendingActivities(
      props.panelId,
      conversation,
      isSpam,
      props.sendingActivities,
      cellsContext,
      timelineVariation,
      lastTimelineMessage,
      lastActivity,
      last(orphanSentMessages)?.uid,
      props.replyChannel,
    ),
    maybeRenderCommentPrecog(
      props.commentingTeammates,
      props.lastActivityCommentAuthorId,
      props.hasSendingMessageActivity,
      props.timelineVariation,
    ),
    maybeRenderChatPrecog(props),
    renderStaticActivities(isInTeamContext, conversation, props.activities),
    maybeRenderDuplicateConversationBanner(props),
    maybeRenderLastSynced(props, isConnected),
    maybeRenderMessagePrecog(props.replyingTeammates, composeDraftMessages, props.timelineVariation),
    maybeRenderDraftSeed(
      props.replyingTeammates,
      composeDraftMessages,
      props.draftSeedAuthors,
      props.timelineVariation,
    ),
    maybeRenderRecentCommunicationsBanner(props),
    renderTimelineDrafts(props),
  );
}

function renderSendingActivities(
  panelId: ViewerPanelId,
  conversation: ConversationModel,
  isSpam: boolean,
  sendingActivities: ReadonlyArray<SendingActivity>,
  cellsContext: CellsContext,
  timelineVariation: TimelineVariationsEnum,
  lastTimelineMessage: LastTimelineMessage,
  lastActivity: ActivityModel | undefined,
  lastOrphanSentMessageUid: string | undefined,
  replyChannel: ChannelModel | undefined,
) {
  return sendingActivities.map((s, index, array) => (
    <SendingActivityViewer
      key={s.uid}
      panelId={panelId}
      cellsContext={cellsContext}
      namespace={conversation.namespace}
      conversationId={conversation.id}
      conversationActionContext={makeConversationActionContext(cellsContext, conversation)}
      isSpam={isSpam}
      replyChannel={lastTimelineMessage.sendingActivityIndex === index ? replyChannel : undefined}
      sendingActivity={s}
      previousSendingActivity={array[index - 1]}
      nextSendingActivity={array[index + 1]}
      isLastActivity={index === array.length - 1}
      lastActivity={lastActivity}
      lastOrphanSentMessageUid={lastOrphanSentMessageUid}
      timelineVariation={timelineVariation}
    />
  ));
}

function renderStaticActivities(
  isInTeamContext: boolean,
  conversation: ConversationModel,
  activities: ReadonlyArray<ActivityModel>,
) {
  // Only render static activities for team conversations in the teammate context.
  if (isInTeamContext) {
    return [];
  }

  return <StaticViewerContainer key="timeline-static" conversation={conversation} activities={activities} />;
}

function renderLoader(page: ViewerPanelConversationPageState, direction: ScrollDirectionsEnum) {
  const isTop = direction === ScrollDirectionsEnum.TOP;
  const moreStatus = isTop ? page.moreStatusTop : page.moreStatusBottom;

  if (moreStatus === AsyncMoreStatusesEnum.HAS_MORE) {
    return (
      <StyledLoaderDiv>
        <Loader size={VisualSizesEnum.SMALL} variant={LoaderVariantsEnum.GREY} isEnabled={false} />
      </StyledLoaderDiv>
    );
  }

  if (moreStatus === AsyncMoreStatusesEnum.LOADING) {
    return (
      <StyledLoaderDiv>
        <Loader size={VisualSizesEnum.SMALL} variant={LoaderVariantsEnum.GREY} />
      </StyledLoaderDiv>
    );
  }

  return null;
}

function maybeRenderCommentPrecog(
  commentingTeammates: ReadonlyArray<TeammateModel>,
  lastActivityCommentAuthorId: number | undefined,
  hasSendingMessageActivity: boolean,
  timelineVariation: TimelineVariationsEnum,
) {
  if (commentingTeammates.length === 0) {
    return [];
  }

  const shouldHideTitle =
    !hasSendingMessageActivity && onlyItem(commentingTeammates)?.id === lastActivityCommentAuthorId;

  return (
    <CommentPrecog
      key="comment-precog"
      teammates={commentingTeammates}
      shouldHideTitle={shouldHideTitle}
      timelineVariation={timelineVariation}
    />
  );
}

function maybeRenderChatPrecog(props: ConversationTimelineContentProps) {
  const {hasChatPrecog, conversation, page, timelineVariation} = props;
  if (!hasChatPrecog) {
    return [];
  }

  return (
    <ChatPrecogContainer
      key="chat-precog"
      conversation={conversation}
      teammateId={page.cellsContext.teammateId}
      timelineVariation={timelineVariation}
    />
  );
}

function maybeRenderMessagePrecog(
  replyingTeammates: ReadonlyArray<TeammateModel>,
  draftComposeMessages: ReadonlyArray<DraftMessageModel>,
  timelineVariation: TimelineVariationsEnum,
) {
  const draftAuthorIds = draftComposeMessages.map((d) => d.authorId);
  const replyingTeammatesWithoutDraft = replyingTeammates.filter((t) => !draftAuthorIds.includes(t.id));
  if (replyingTeammatesWithoutDraft.length === 0) {
    return [];
  }

  return (
    <MessagePrecog
      key="message-precog"
      teammates={replyingTeammatesWithoutDraft}
      timelineVariation={timelineVariation}
      areTeammatesReplying
    />
  );
}

function maybeRenderDraftSeed(
  replyingTeammates: ReadonlyArray<TeammateModel>,
  draftComposeMessages: ReadonlyArray<DraftMessageModel>,
  draftSeedAuthors: ReadonlyArray<TeammateModel>,
  timelineVariation: TimelineVariationsEnum,
) {
  const draftAuthorIds = draftComposeMessages.map((d) => d.authorId);
  const replyingTeammateIds = replyingTeammates.map((t) => t.id);

  const draftSeedAuthorsWithoutPrecogOrDraft = draftSeedAuthors.filter(
    (t) => !draftAuthorIds.includes(t.id) && !replyingTeammateIds.includes(t.id),
  );

  if (draftSeedAuthorsWithoutPrecogOrDraft.length === 0) {
    return [];
  }

  return (
    <MessagePrecog
      key="draft-seed-precog"
      teammates={draftSeedAuthorsWithoutPrecogOrDraft}
      timelineVariation={timelineVariation}
    />
  );
}

function maybeRenderGoOnlineBanner(props: ConversationTimelineContentProps, isConnected: boolean) {
  if (isConnected) {
    return null;
  }

  if (props.page.moreStatusTop === AsyncMoreStatusesEnum.ALL_LOADED) {
    return null;
  }

  return <GoOnlineBanner key="go-online" renderLocation={GoOnlineBannerRenderLocationsEnum.P3} />;
}

/** Although split conversation exists as an activity, we render it as a static banner. */
function maybeRenderSplitConversationBanner(props: ConversationTimelineContentProps) {
  // Find the first split conversation activity.
  const activity = findSplitConversationActivity(props.activities);
  if (!activity) {
    return null;
  }

  const isSplitForPrivacy =
    activity.source.type === ActivitySourceTypesEnum.DATA_LEAK &&
    activity.source.visualType === ActivitySourceVisualTypesEnum.OTHER;

  return (
    <SplitConversationBannerContainer
      key="split_conversation_banner"
      activity={activity}
      isSplitForPrivacy={isSplitForPrivacy}
      onDataLeakLearnMoreClick={onDataLeakLearnMoreClick}
    />
  );
}

function maybeRenderDuplicateConversationBanner(props: ConversationTimelineContentProps) {
  const {id: conversationId, duplicateConversationIds, shouldShowDuplicateBanner} = props.conversation;

  if (duplicateConversationIds.length === 0) {
    return [];
  }

  return (
    <DuplicateConversationBannerContainer
      key="duplicate-conversation-banner"
      duplicateConversationIds={duplicateConversationIds}
      conversationId={conversationId}
      shouldBannerHaveBeenHidden={!shouldShowDuplicateBanner}
    />
  );
}

function maybeRenderDemoConversationBanner(props: ConversationTimelineContentProps) {
  // Only render the banner if either the personalized demo inbox is active or
  // the invited teammate welcome message is active.
  if (!props.isPersonalizedDemoInboxActive) {
    return null;
  }

  // If there are additional activities on the welcome message, remove the banner
  if (props.activities.length > 1) {
    return null;
  }

  return <DemoConversationBanner />;
}

function maybeRenderRecentCommunicationsBanner(props: ConversationTimelineContentProps) {
  const {
    conversation,
    hasRecentCommunicationsBanner,
    hasRecentCommunicationsComposerBanner,
    page,
    userComposeDraftMessages,
  } = props;

  // Check for the recent-communications-banner feature flag
  if (!hasRecentCommunicationsBanner) {
    return [];
  }

  // If we're showing the recent communications banner in the composer, don't show it in the conversation timeline
  if (hasRecentCommunicationsComposerBanner) {
    return [];
  }

  // We only want to show the banner if the user is composing a reply
  if (userComposeDraftMessages.length < 1) {
    return [];
  }

  // We don't want to show the banner if there's no recipient or if the main recipient is BCC'd
  const conversationRecipient = conversation.recipient;
  if (!conversationRecipient || conversationRecipient.role === RecipientRolesEnum.BCC) {
    return [];
  }

  return (
    <RecentCommunicationsBannerContainer
      key="recent-communications-banner"
      conversation={conversation}
      teammateId={page.cellsContext.teammateId}
      shouldRenderAsComposerBanner={false}
    />
  );
}

function maybeRenderLastSynced(props: ConversationTimelineContentProps, isConnected: boolean) {
  if (isConnected) {
    return [];
  }

  return <LastSyncedActivity key="last-synced" lastSyncedDate={props.fetchedAt} />;
}

function renderBottomDrafts(
  props: ConversationTimelineContentProps,
  bottomComposerRef: RefCallback<HTMLDivElement>,
  bottomComposerHeight: number,
) {
  if (props.timelineVariation !== TimelineVariationsEnum.SYNCHRONOUS) {
    return <Fragment key="message-drafts-box-synchronous" />;
  }
  const sharedDraftMessages = props.composeDraftMessages.filter((draft) => draft.isShared);

  // If the user has no actively authored drafts, show other shared drafts in BottomMessageDrafts
  // Private drafts from teammates will be rendered by renderTimelineDrafts
  const draftsToRender =
    props.userComposeDraftMessages.length === 0 ? sharedDraftMessages : props.userComposeDraftMessages;

  return (
    <Fragment key="message-drafts-box-synchronous">
      <BottomMessageDrafts
        {...props}
        composeDraftMessages={draftsToRender}
        bottomComposerRef={bottomComposerRef}
        bottomComposerHeight={bottomComposerHeight}
      />
    </Fragment>
  );
}

function renderTimelineSynchronousDrafts(props: ConversationTimelineContentProps) {
  return (
    <Fragment key="message-drafts-box">
      <MessageDrafts {...props} />
    </Fragment>
  );
}

function renderTimelineAsynchronousDrafts(props: ConversationTimelineContentProps) {
  const {panelId, userId, conversation, page, focusKey, isSpam} = props;

  return props.composeDraftMessages.map((draft) => {
    // Shared draft from other teammate OR readonly draft.
    const isDraftShared = isMessageVisuallyShared(draft, conversation, props.hasAutoShareDrafts);

    const isDraftInReadMode = computeIsDraftInReadMode(
      draft,
      userId,
      props.readonlyDraftMessageUids,
      isDraftShared,
    );

    if (isDraftInReadMode) {
      return (
        <MessageViewerContainer
          key={draft.uid}
          panelId={panelId}
          messageUid={draft.uid}
          cellsContext={page.cellsContext}
          namespace={conversation.namespace}
          conversationId={conversation.id}
          conversationActionContext={makeConversationActionContext(page.cellsContext, conversation)}
          isSpam={isSpam}
          isAuthorReplying={props.replyingTeammates.some((r) => r.id === draft.authorId)}
          isSharedDraft
          isFirstMessage={props.activities.length === 0}
          timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
        />
      );
    }

    // Composer draft.
    return (
      <MessageComposerContainer
        key={draft.uid}
        panelId={panelId}
        message={draft}
        cellsContext={page.cellsContext}
        dontFocusComposer={props.dontFocusComposer || focusKey !== draft.uid}
        conversation={conversation}
        isFirst={props.activities.length === 0}
        timelineVariation={TimelineVariationsEnum.ASYNCHRONOUS}
      />
    );
  });
}

function renderTimelineDrafts(props: ConversationTimelineContentProps) {
  // If asynchronous, render all drafts in the timeline
  if (props.timelineVariation === TimelineVariationsEnum.ASYNCHRONOUS) {
    return renderTimelineAsynchronousDrafts(props);
  }

  const teammateAuthoredDrafts = props.composeDraftMessages.filter(
    (draft) => draft.authorId !== props.userId,
  );

  const teammatePrivateAuthoredDrafts = teammateAuthoredDrafts.filter((draft) => !draft.isShared);

  // If the user has no actively authored drafts, do not render teammate shared drafts in the timeline
  // They will be rendered by renderBottomDrafts instead
  // Private drafts from teammates should still appear in the timeline
  if (props.userComposeDraftMessages.length === 0) {
    return renderTimelineSynchronousDrafts({...props, composeDraftMessages: teammatePrivateAuthoredDrafts});
  }

  // Otherwise, render only drafts that are not authored by the user in the timeline
  return renderTimelineSynchronousDrafts({...props, composeDraftMessages: teammateAuthoredDrafts});
}

/*
 * Helpers.
 */

function findCanceledReminderActivity(
  activities: ReadonlyArray<ActivityModel>,
  canceledReminder?: ReminderModel,
  lastMessageActivityId?: number,
) {
  if (!canceledReminder) {
    return undefined;
  }

  const canceledReminderActivity = activities.find(
    (activity) =>
      activity.visualType === ActivityVisualTypesEnum.MESSAGE &&
      activity.messageId === canceledReminder.canceledById,
  );

  if (canceledReminder.isCanceledByActivity) {
    return _.last(activities)?.id;
  }

  return (canceledReminderActivity && canceledReminderActivity.id) || lastMessageActivityId;
}

function findFocusMargin(props: ConversationTimelineContentProps, focusKey?: Key) {
  const defaultFocusMargin = 16;

  // If we're not focusing a "new activity" line, nothing to do.
  if (focusKey !== MILESTONE_ACTIVITY) {
    return defaultFocusMargin;
  }

  const {page, activities, focusMode} = props;

  // If we're not focusing from the top, nothing to do.
  if (focusMode !== FocusModesEnum.TOP) {
    return defaultFocusMargin;
  }

  // If we don't have an originalSeenAt, nothing to do.
  const originalSeenAt = page.originalConversationState?.seenAt;
  if (!originalSeenAt) {
    return defaultFocusMargin;
  }

  const firstUnreadActivity = activities.find(
    (a) => isActivityInConversationRelevant(a) && a.date > originalSeenAt,
  );

  if (!firstUnreadActivity) {
    return defaultFocusMargin;
  }

  // If our first unread activity isn't a message, nothing to do.
  if (firstUnreadActivity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
    return defaultFocusMargin;
  }

  // Otherwise, return a bigger margin to show the (collapsed) message in the viewport.
  return 72;
}

/** Find the first split conversation activity available. */
function findSplitConversationActivity(
  activities: ReadonlyArray<ActivityModel>,
): BasicActivitySplitTargetModel | undefined {
  return activities.find(
    (a) =>
      a.type === BasicActivityTypesEnum.SPLIT_TARGET &&
      a.visualType === ActivityVisualTypesEnum.BASIC &&
      a.basicVisualType === BasicVisualTypesEnum.SPLIT_TARGET &&
      a,
  ) as BasicActivitySplitTargetModel | undefined;
}

function onDataLeakLearnMoreClick() {
  openInBrowser(helpCenterDataLeakUrl);
}

/* Returns whether the given activity is a message that contains a new calendar event. */
function containsNewCalendarEvent(props: ConversationTimelineProps, activity: ActivityModel) {
  if (activity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
    return false;
  }

  return props.messageUidsWithNewCalendarEvents.has(activity.messageUid);
}
